<template>
  <!-- Mobile drawer -->
  <v-navigation-drawer
    v-model="drawerOpen"
    temporary
    location="left"
    class="nav-drawer"
  >
    <div class="nav-drawer__header">
      <span class="nav-brand__mark">27pub</span>
      <span class="nav-brand__sub">Poker Management</span>
      <v-btn icon density="compact" variant="text" class="nav-drawer__close" @click="drawerOpen = false">
        <v-icon color="white">mdi-close</v-icon>
      </v-btn>
    </div>

    <v-list class="nav-drawer__list">
      <template v-if="loggedIn">
        <v-list-item
          v-if="hasPokerPermission"
          :to="{ name: 'SessionEntry' }"
          prepend-icon="mdi-cards-playing"
          title="Session Entry"
          @click="drawerOpen = false"
        />
        <v-list-item
          v-if="hasTournamentPermission"
          :to="{ name: 'TournamentView' }"
          prepend-icon="mdi-trophy"
          title="Tournament Timer"
          @click="drawerOpen = false"
        />
        <v-list-item
          v-if="hasTournamentPermission"
          :to="{ name: 'LeagueList' }"
          prepend-icon="mdi-podium"
          title="Leagues"
          @click="drawerOpen = false"
        />
        <v-list-item
          :to="{ name: 'UserProfile', params: { userId: userStore.userid } }"
          prepend-icon="mdi-account"
          title="Profile"
          @click="drawerOpen = false"
        />
        <v-list-item
          :to="{ name: 'UserFeedback' }"
          prepend-icon="mdi-message-text"
          title="Feedback"
          @click="drawerOpen = false"
        />
        <v-list-item
          v-if="isAdmin"
          :to="{ name: 'AdminView' }"
          prepend-icon="mdi-shield-crown"
          title="Admin"
          @click="drawerOpen = false"
        >
          <template v-if="unreadFeedbackCount > 0" #append>
            <v-badge :content="unreadFeedbackCount" color="error" inline />
          </template>
        </v-list-item>
        <v-divider class="my-2" />
        <v-list-item
          prepend-icon="mdi-logout"
          title="Logout"
          @click="handleLogout"
        />
      </template>
      <template v-else>
        <v-list-item
          :to="{ name: 'UserLogin' }"
          prepend-icon="mdi-login"
          title="Sign In"
          @click="drawerOpen = false"
        />
        <v-list-item
          :to="{ name: 'UserSignup' }"
          prepend-icon="mdi-account-plus"
          title="Join the Beta"
          @click="drawerOpen = false"
        />
      </template>
    </v-list>
  </v-navigation-drawer>

  <!-- App bar -->
  <v-app-bar app color="primary" dark>
    <!-- Hamburger — mobile only -->
    <v-app-bar-nav-icon
      class="d-flex d-md-none"
      @click="drawerOpen = !drawerOpen"
    />

    <v-toolbar-title>
      <router-link :to="{ name: 'HomePage' }" class="nav-brand">
        <span class="nav-brand__mark">27pub</span>
        <span class="nav-brand__sub d-none d-sm-inline">Poker Management</span>
      </router-link>
    </v-toolbar-title>

    <v-spacer />

    <!-- Desktop nav links — hidden on mobile -->
    <template v-if="loggedIn">
      <v-btn v-if="hasPokerPermission" variant="text" :to="{ name: 'SessionEntry' }" class="d-none d-md-flex">Session Entry</v-btn>
      <v-btn v-if="hasTournamentPermission" variant="text" :to="{ name: 'TournamentView' }" class="d-none d-md-flex">Tournament Timer</v-btn>
      <v-btn v-if="hasTournamentPermission" variant="text" :to="{ name: 'LeagueList' }" class="d-none d-md-flex">Leagues</v-btn>
      <v-btn variant="text" :to="{ name: 'UserProfile', params: { userId: userStore.userid } }" class="d-none d-md-flex">Profile</v-btn>
      <v-btn variant="text" :to="{ name: 'UserFeedback' }" class="d-none d-md-flex">Feedback</v-btn>
      <v-btn v-if="isAdmin" variant="text" :to="{ name: 'AdminView' }" class="d-none d-md-flex">
        Admin
        <v-badge v-if="unreadFeedbackCount > 0" :content="unreadFeedbackCount" color="error" inline class="ml-1" />
      </v-btn>
      <v-btn variant="text" @click="handleLogout" class="d-none d-md-flex">Logout</v-btn>
    </template>
    <template v-else>
      <v-btn variant="text" :to="{ name: 'UserLogin' }" class="d-none d-md-flex">Login</v-btn>
      <v-btn variant="text" :to="{ name: 'UserSignup' }" class="d-none d-md-flex">Sign Up</v-btn>
    </template>
  </v-app-bar>
</template>

<script setup>
import { computed, ref, watch, onUnmounted } from 'vue';
import { useUserStore } from '@/stores/users';
import { useRouter } from 'vue-router';
import { logout as apiLogout } from '@/api/lists.js';
import { db } from '@/firebaseConfig';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { FEEDBACK_DB } from '@/constants/constants';

const userStore = useUserStore();
const router = useRouter();
const drawerOpen = ref(false);

const loggedIn = computed(() => !!userStore.loggedin);
const hasPokerPermission = computed(() => !!(userStore.user?.permissions?.poker === 'approved'));
const hasTournamentPermission = computed(() => !!(userStore.user?.permissions?.tournament === 'approved'));
const isAdmin = computed(() => !!(userStore.user?.permissions?.admin));

const unreadFeedbackCount = ref(0);
let feedbackUnsub = null;

watch(isAdmin, (admin) => {
  if (feedbackUnsub) {
    feedbackUnsub();
    feedbackUnsub = null;
    unreadFeedbackCount.value = 0;
  }
  if (admin) {
    const q = query(collection(db, FEEDBACK_DB), where('status', '==', 'New'));
    feedbackUnsub = onSnapshot(q, snap => {
      unreadFeedbackCount.value = snap.size;
    }, () => {
      unreadFeedbackCount.value = 0;
    });
  }
}, { immediate: true });

onUnmounted(() => {
  if (feedbackUnsub) feedbackUnsub();
});

const handleLogout = async () => {
  drawerOpen.value = false;
  try {
    await apiLogout();
    userStore.setUser(null);
    router.push({ name: 'UserLogin' });
  } catch (err) {
    console.error('Error during logout:', err);
  }
};
</script>

<style scoped>
.nav-brand {
  display: flex;
  align-items: baseline;
  gap: 8px;
  text-decoration: none;
}
.nav-brand__mark {
  font-family: 'DM Sans', sans-serif;
  font-weight: 700;
  font-size: 1.2rem;
  color: white;
  letter-spacing: -0.3px;
}
.nav-brand__sub {
  font-family: 'DM Mono', monospace;
  font-size: 0.65rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: rgba(255, 255, 255, 0.75);
}
.v-btn {
  color: white !important;
}

/* Drawer */
.nav-drawer__header {
  background: #2D6A4F;
  padding: 20px 16px 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  position: relative;
}
.nav-drawer__header .nav-brand__mark {
  font-size: 1.4rem;
}
.nav-drawer__close {
  position: absolute;
  top: 12px;
  right: 8px;
}
.nav-drawer__list {
  padding-top: 8px;
}
</style>
